// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useRegistrarService from '@hooks/queries/registry/onboarding/useRegistrarService';
import { BurnRequest } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/Burn';
import { BurnRequest as BurnRequest3Step } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Burn';
import { ContractId } from '@daml/types';
import { RowNew } from '../../../../components/Table/types';
import { useAcceptBurnRequest3Step } from './useAcceptBurnRequest';
import { useAcceptBurn2Step } from './useAcceptBurnRequest2Step';
import { useCancelBurnRequest3Step } from './useCancelBurnRequest';
import { useCancelBurnRequest2Step } from './useCancelBurnRequest2Step';
import { useRejectBurnRequest3Step } from './useRejectBurnRequest';
import { useRejectBurnRequest2Step } from './useRejectBurnRequest2Step';

type ContractId3Step = ContractId<BurnRequest3Step>;
type ContractId2Step = ContractId<BurnRequest>;

export const useGetBurnChoices = (onSuccess?: () => void, selectedRow?: RowNew) => {
  const registrarService = useRegistrarService();

  // 2 step
  const acceptBurnRequest2Step = useAcceptBurn2Step();
  const cancelBurnRequest2Step = useCancelBurnRequest2Step();
  const rejectBurnRequest2Step = useRejectBurnRequest2Step();

  // 3 step
  const acceptBurnRequest3Step = useAcceptBurnRequest3Step();
  const cancelBurnRequest3Step = useCancelBurnRequest3Step();
  const rejectBurnRequest3Step = useRejectBurnRequest3Step();
  const registrarServiceCid = registrarService?.data?.contractId;
  const contractId = selectedRow?.contractId;
  const isThreeStep = selectedRow?.isThreeStep;
  const onAccept = async () => {
    if (!contractId) {
      throw new Error('No row selected');
    }
    if (isThreeStep) {
      if (!registrarServiceCid) {
        throw new Error('No registrar service');
      }
      await acceptBurnRequest3Step({
        cid: registrarServiceCid,
        argument: { cid: contractId as ContractId3Step, payload: {} },
      });
    } else {
      await acceptBurnRequest2Step({ burnRequestId: contractId as ContractId2Step });
    }
    onSuccess?.();
  };

  const onReject = async (reason: string) => {
    if (!contractId) {
      throw new Error('No row selected');
    }
    if (isThreeStep) {
      if (!registrarServiceCid) {
        throw new Error('No registrar service');
      }
      await rejectBurnRequest3Step({
        cid: registrarServiceCid,
        argument: { cid: contractId as ContractId3Step, payload: { reason } },
      });
    } else {
      await rejectBurnRequest2Step({
        burnRequestId: contractId as ContractId2Step,
        reason,
      });
    }
    onSuccess?.();
  };

  const onCancel = async () => {
    if (!contractId) {
      throw new Error('No row selected');
    }
    if (isThreeStep) {
      if (!registrarServiceCid) {
        throw new Error('No registrar service');
      }
      await cancelBurnRequest3Step({
        cid: contractId as ContractId3Step,
        argument: {},
      });
    } else {
      await cancelBurnRequest2Step({
        burnRequestId: contractId as ContractId2Step,
      });
    }
    onSuccess?.();
  };
  return {
    onAccept,
    onReject,
    onCancel,
  };
};
